import type { JSX, ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAuth } from "./AuthProvider.js";
import { returnPathOf } from "./sessionExpiry.js";
import { FullPageSpinner } from "./FullPageSpinner.js";

/**
 * Route guard.
 *
 * The `loading` branch is not cosmetic. `AuthProvider` starts with
 * `user === null` and asks the BFF who it is on mount; without the wait, a
 * page refresh on a live session renders null for one frame, redirects to
 * /login, and throws the user out of the app every time they hit reload.
 *
 * This is a UX guard, not a security boundary — it decides what to RENDER.
 * Nothing behind it is trusted client-side: every data call carries the
 * httpOnly session cookie and is authorised again by the BFF, which mints
 * a fresh CRM token per request that crm-api's scope wall then judges.
 *
 * An APPLICANT (spec E §10.2) is sent to /onboarding — the one place their
 * cookie opens.
 */
export function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const { user, applicant, status } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return <FullPageSpinner />;
  }

  if (!user) {
    // An applicant has a session — just not one that opens any of this.
    // The wall is theirs; sending them to /login would sign them in to the
    // wall anyway, one screen later.
    if (applicant) return <Navigate to="/onboarding" replace />;
    // `returnPathOf`, not `location.pathname`: the query is part of the
    // address for the invoice editor — `?invoiceId=31` is what makes the page
    // "edit invoice 31" rather than "start a blank one". Shared with
    // `SessionExpiryBridge` so both routes into /login carry the same shape.
    return <Navigate to="/login" replace state={{ from: returnPathOf(location) }} />;
  }

  return <>{children}</>;
}
